import type { ScreenplayEditorBlock } from './screenplayEditor';
import type { SentenceAudioStatus } from './audioReadiness';
import { estimatedScreenplayPages } from './screenplayPages';

// Planning assumptions only: never run AI or inspect dramatic content to quote.
export function genericAnalysisEstimate(pages: number) {
  return { seconds: Math.max(1,pages)*10, dollars: Math.max(1,pages)*0.01 };
}
export function analyzedPageCount(blocks: ScreenplayEditorBlock[], pages: number, done: number, total: number, lastEntryId?: string) {
  if (done >= total && total > 0) return pages;
  if (!done || !lastEntryId) return 0;
  // A page is complete only once analysis has passed into the following page.
  return Math.min(pages,Math.max(0,(estimatedScreenplayPages(blocks).starts.get(lastEntryId)??1)-1));
}
/** Each scene requires ALL its current playback/announcement clips, not the
 * optional neutral cache copies that never appear in playback/export.
 * Whole cached blocks can span scene boundaries; all affected scenes wait.
 * No verified item snapshot means unknown, never a made-up zero completion. */
export function sceneAudioProgress(blocks: ScreenplayEditorBlock[], states: SentenceAudioStatus[] = [], pageLimit?: number) {
  const starts=estimatedScreenplayPages(blocks).starts;
  const byBlock=new Map<string,number>();let scene=0,seenHeading=false;
  for(const block of blocks){
    if(pageLimit!==undefined&&(starts.get(block.id)??Infinity)>pageLimit)break;
    // Match the editor's heading numbers; introductory narration belongs to
    // scene one, rather than introducing an invisible extra numbered scene.
    if(block.type==='scene-heading'){if(seenHeading)scene++;seenHeading=true;}
    byBlock.set(block.id,scene);
  }
  const scenes=new Map<number,{complete:boolean;started:boolean;attention:boolean}>();
  const neutralCache={completed:0,total:0};
  for(const state of states){
    const ids=(state.blockIds??[state.blockId]).filter(id=>byBlock.has(id));
    if(!ids.length)continue;
    const ready=state.state==='ready'&&state.verified;
    if(state.id.startsWith('baseline:')){
      neutralCache.total++;if(ready)neutralCache.completed++;
      continue;
    }
    for(const id of ids){
      const number=byBlock.get(id)!;
      const previous=scenes.get(number)??{complete:true,started:false,attention:false};
      scenes.set(number,{complete:previous.complete&&ready,
        attention:previous.attention||state.state==='failed'||state.state==='repair_required',
        started:previous.started||ready||state.state==='generating'||state.state==='saving'});
    }
  }
  return {total:scenes.size,completed:[...scenes.values()].filter(s=>s.complete).length,
    inProgress:[...scenes.values()].filter(s=>s.started&&!s.complete&&!s.attention).length,
    needsAttention:[...scenes.values()].filter(s=>s.attention).length,neutralCache};
}
export function convertedScenes(blocks: ScreenplayEditorBlock[], states: SentenceAudioStatus[] = [], pageLimit?: number) {
  const {total,completed}=sceneAudioProgress(blocks,states,pageLimit);
  return {total,completed};
}

/** Count source script blocks, not fragments, duplicate clips or neutral cache copies. */
export function playbackBlockProgress(ids: readonly string[], states: SentenceAudioStatus[] = []) {
  const blocks=new Map([...new Set(ids)].map(id=>[id,{seen:false,ready:true,known:true,attention:false}]));
  for(const state of states){
    if(state.id.startsWith('baseline:'))continue;
    for(const id of state.blockIds??[state.blockId]){
      const block=blocks.get(id);if(!block)continue;
      block.seen=true;block.ready&&=state.state==='ready'&&state.verified;
      block.known&&=!['syncing','unknown'].includes(state.state);
      block.attention||=state.state==='failed'||state.state==='repair_required';
    }
  }
  const values=[...blocks.values()];
  return {total:values.length,completed:values.filter(b=>b.seen&&b.ready).length,
    checking:values.some(b=>!b.seen||!b.known),needsAttention:values.filter(b=>b.attention).length};
}

// Only non-secret IDs/digests are persisted, never screenplay text or results.
// Reopening/reconfirming the same saved input reuses the same recovery allowance.
export function conversionAnalysisRun(owner: string, screenplay: string, signature: string) {
  const key=`tableread.conversion-analysis.${owner}.${screenplay}`;
  try {
    const previous=JSON.parse(localStorage.getItem(key)??'null');
    if(previous?.signature===signature&&typeof previous.id==='string'&&/^[a-f0-9-]{36}$/i.test(previous.id))return previous.id as string;
  }catch{/* Storage may be unavailable; server still enforces per-run bounds. */}
  const id=crypto.randomUUID();
  try{localStorage.setItem(key,JSON.stringify({id,signature}));}catch{/* In-memory id remains stable for this confirmation. */}
  return id;
}
